import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Droplets, Bell, UserCircle, X, Camera, LogOut, AlertTriangle, AlertCircle } from 'lucide-react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useAlertFeed } from '../context/AlertContext';
import '../styles/Header.css'; // Import the separated CSS

export default function Header() {
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const { user } = useUser();
    const { signOut } = useClerk();
    const { alerts } = useAlertFeed();
    const role = String(user?.publicMetadata?.role || '').trim().toLowerCase();
    const canViewAdmin = role === 'agm' || role === 'administrator';

    // Logout handler - signs out from Clerk
    const handleLogout = () => {
        signOut();
        setIsProfileOpen(false);
    };

    // Profile button handler
    const handleProfileClick = () => {
        setIsProfileOpen(true);
    };

    return (
        <>
            {/* --- Main Header --- */}
            <header className="app-header">
                <div className="header-container">

                    {/* Logo */}
                    <div className="logo-group">
                        <Droplets className="logo-icon" />
                        <span className="logo-text">ChloroCast</span>
                    </div>

                    {/* Nav Links */}
                    <nav className="main-nav">
                        <NavLink
                            to="/"
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                        >
                            Dashboard
                        </NavLink>
                        <NavLink
                            to="/dataentry"
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                        >
                            Data Entry
                        </NavLink>
                        <NavLink
                            to="/prediction"
                            className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                        >
                            Prediction
                        </NavLink>
                        {canViewAdmin && (
                            <NavLink
                                to="/admin"
                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                            >
                                Admin
                            </NavLink>
                        )}
                    </nav>

                    {/* Right Icons */}
                    <div className="header-actions">
                        <button
                            className="icon-btn"
                            onClick={() => setIsNotificationOpen(prev => !prev)}
                            aria-label="Open alert notifications"
                        >
                            <Bell className="w-5 h-5" />
                            {alerts.length > 0 && (
                                <span className="notification-count">{alerts.length}</span>
                            )}
                        </button>
                        {/* Profile button */}
                        <button onClick={handleProfileClick} className="icon-btn">
                            <UserCircle className="w-6 h-6" />
                        </button>
                    </div>

                    {isNotificationOpen && (
                        <div className="notification-dropdown" role="dialog" aria-label="Alert log notifications">
                            <div className="notification-header">
                                <h3>Alert Log</h3>
                                <button
                                    type="button"
                                    className="notification-close-btn"
                                    onClick={() => setIsNotificationOpen(false)}
                                    aria-label="Close notifications"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            {alerts.length === 0 ? (
                                <p className="notification-empty">No alerts in the last hour.</p>
                            ) : (
                                <ul className="notification-alert-list">
                                    {alerts.map((alert, index) => {
                                        const itemTypeClass = alert?.type === 'red' ? 'notification-alert-red' : 'notification-alert-yellow';
                                        const Icon = alert?.type === 'red' ? AlertTriangle : AlertCircle;

                                        return (
                                            <li className={`notification-alert-item ${itemTypeClass}`} key={`${alert?.title || 'alert'}-${index}`}>
                                                <Icon className="w-4 h-4" />
                                                <div>
                                                    <p className="notification-alert-title">{alert?.title || 'Alert'}</p>
                                                    <p className="notification-alert-desc">{alert?.desc || 'No description provided.'}</p>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    )}

                </div>
            </header>

            {/* --- Profile Modal Overlay --- */}
            {isProfileOpen && user && (
                <div className="modal-overlay">
                    <div className="modal-content">

                        {/* Modal Header */}
                        <div className="modal-header">
                            <h2 className="modal-title">My profile</h2>
                            <button onClick={() => setIsProfileOpen(false)} className="close-btn">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="modal-body">
                            <div className="profile-grid">

                                {/* Left Column: Profile Card */}
                                <div className="col-span-1">
                                    <div className="profile-card">
                                        <div className="avatar-wrapper">
                                            <div className="avatar-circle">
                                                {user.firstName?.charAt(0) || 'U'}
                                            </div>
                                            <button className="camera-btn">
                                                <Camera className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <h3 className="user-name">{`${user.firstName} ${user.lastName}`}</h3>
                                        <p className="user-email">{user.emailAddresses[0]?.emailAddress}</p>
                                        <span className="status-badge">
                                            Active
                                        </span>
                                    </div>
                                </div>

                                {/* Right Column: General Info */}
                                <div className="col-span-2">
                                    <div className="section-card">
                                        <h3 className="section-title">General Information</h3>
                                        <div className="form-row">
                                            <div className="form-group">
                                                <label className="form-label">First Name</label>
                                                <input type="text" defaultValue={user.firstName || ''} className="form-input" disabled />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">Last Name</label>
                                                <input type="text" defaultValue={user.lastName || ''} className="form-input" disabled />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label className="form-label">Email</label>
                                            <input type="text" defaultValue={user.emailAddresses[0]?.emailAddress || ''} disabled className="form-input" />
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="modal-footer">
                            <button
                                onClick={handleLogout}
                                className="btn-logout"
                            >
                                <LogOut className="w-4 h-4" />
                                Log Out
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </>
    );
}